import { useState, useEffect, useCallback } from "react";
import { useLang, LangSelector } from "../LangContext";
import { supabase } from "../supabase";
import { CSS } from "../styles";
import { ROLE_META, ROLE_LABELS } from "../constants";
import { DEFAULT_TODOS, DEFAULT_MATERIALS, DEFAULT_BUDGET } from "../defaultData";

export default function ProjectList({ profile, onSelect, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState(""); const [newAddress, setNewAddress] = useState(""); const [newClient, setNewClient] = useState(""); const [newDeadline, setNewDeadline] = useState("");
  const [creating, setCreating] = useState(false);
  const { t } = useLang();
  const [showJoin, setShowJoin] = useState(false); const [joinCodeInput, setJoinCodeInput] = useState(""); const [joining, setJoining] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };
  const rm = ROLE_META[profile.role];

  const loadProjects = useCallback(async () => {
    const { data: owned } = await supabase.from("projects").select("*").eq("owner_id", profile.id).order("created_at", { ascending: false });
    const { data: member } = await supabase.from("project_members").select("project_id, projects(*)").eq("user_id", profile.id);
    const memberProjects = (member || []).map(m => m.projects).filter(Boolean);
    const all = [...(owned || []), ...memberProjects].filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
    setProjects(all); setLoading(false);
  }, [profile.id]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const genCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  const createProject = async () => {
    if (!newName.trim()) return;
    setCreating(true);

    // Retry loop — garantit un join_code unique
    let data = null;
    let attempts = 0;
    while (!data && attempts < 5) {
      attempts++;
      const joinCode = genCode();
      const { data: inserted, error } = await supabase
        .from("projects")
        .insert({
          name: newName.trim(),
          address: newAddress.trim() || null,
          client_name: newClient.trim() || null,
          owner_id: profile.id,
          status: "Brief",
          progress: 0,
          deadline: newDeadline || null,
          join_code: joinCode,
        })
        .select()
        .single();

      if (!error) { data = inserted; break; }
      // Si l'erreur n'est pas une collision de join_code, on arrête
      if (!error.message.includes("join_code")) {
        showToast("❌ Erreur : " + error.message);
        setCreating(false);
        return;
      }
      // Sinon collision → on réessaie avec un nouveau code
    }

    if (!data) {
      showToast("❌ Impossible de générer un code unique. Réessayez.");
      setCreating(false);
      return;
    }

    const { error: dataError } = await supabase.from("project_data").insert([
      { project_id: data.id, section: "todos", data: DEFAULT_TODOS },
      { project_id: data.id, section: "materials", data: DEFAULT_MATERIALS },
      { project_id: data.id, section: "budget", data: DEFAULT_BUDGET },
      { project_id: data.id, section: "journal", data: { entries: [] } },
    ]);
    if (dataError) showToast("⚠ Projet créé mais données initiales manquantes");

    setProjects(prev => [data, ...prev]);
    setShowCreate(false); setNewName(""); setNewAddress(""); setNewClient(""); setNewDeadline("");
    showToast(t("projectCreated"));
    setCreating(false);
  };

  const [showMenu, setShowMenu] = useState(false);

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: "#0F0E0C", minHeight: "100vh", color: "#E8E2D8" }}>
      <style>{CSS}</style>
      <header style={{ borderBottom: "1px solid #2A2924", padding: "0 16px", height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#0F0E0C", zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 26, height: 26, background: "#C9A96E", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>⬡</div>
          <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 15, color: "#E8E2D8" }}>Travea</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Desktop: full info */}
          <div className="hd-extra" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${rm.color}20`, border: `1.5px solid ${rm.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: rm.color }}>{profile.avatar}</div>
            <span style={{ fontSize: 12, color: "#7A7468" }}>{profile.name}</span>
            <span style={{ fontSize: 10, color: rm.color, background: `${rm.color}15`, borderRadius: 4, padding: "2px 8px", fontWeight: 600 }}>{rm.icon} {ROLE_LABELS[profile.role]}</span>
            <LangSelector />
            <button onClick={onLogout} className="btn" style={{ fontSize: 11, color: "#5A5450", border: "1px solid #2A2924", borderRadius: 8, padding: "5px 12px" }}>{t("logout")}</button>
          </div>
          {/* Mobile: avatar + menu */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: `${rm.color}20`, border: `1.5px solid ${rm.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: rm.color }}>{profile.avatar}</div>
            <button className="hd-menu-btn" onClick={() => setShowMenu(true)}>⋯</button>
          </div>
        </div>
      </header>

      {/* Mobile menu sheet */}
      {showMenu && (
        <div className="modal-overlay" onClick={() => setShowMenu(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E2D8" }}>{profile.name}</div>
                <div style={{ fontSize: 11, color: rm.color, marginTop: 2 }}>{rm.icon} {ROLE_LABELS[profile.role]}</div>
              </div>
              <button className="btn" onClick={() => setShowMenu(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "#7A7468", marginBottom: 8 }}>Langue</div>
                <LangSelector />
              </div>
              <button onClick={() => { setShowMenu(false); onLogout(); }} className="btn"
                style={{ width: "100%", padding: "12px", borderRadius: 10, background: "#252420", border: "1px solid #2A2924", color: "#FC8181", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
                {t("logout")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="main-pad">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h1 className="st" style={{ marginBottom: 0 }}>{t("myProjects")}</h1>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowJoin(true)} className="btn" style={{ border: "1px solid #2A2924", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#7A7468" }}>
              + Code projet
            </button>
            {(profile.role === "architect" || profile.role === "contractor") && (
              <button onClick={() => setShowCreate(true)} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600, border: "none" }}>+ Nouveau projet</button>
            )}
          </div>
        </div>
        {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><div className="spinner" /></div>
          : projects.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>⬡</div>
              <div style={{ fontSize: 16, color: "#7A7468", marginBottom: 8 }}>{t("noProjects")}</div>
              <div style={{ fontSize: 13, color: "#5A5450", marginBottom: 24 }}>{profile.role === "architect" ? t("noProjectsDesc_architect") : t("noProjectsDesc_other")}</div>
              {profile.role === "architect" && <button onClick={() => setShowCreate(true)} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: "10px 20px", fontSize: 13, fontWeight: 600, border: "none" }}>+ Créer un projet</button>}
              {profile.role !== "architect" && (
                <div style={{ maxWidth: 320, margin: "0 auto" }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input placeholder={t("joinProjectCode")} id="join-code-input" style={{ flex: 1, textAlign: "center", fontSize: 16, fontWeight: 600, letterSpacing: "0.15em" }} />
                    <button className="btn" onClick={async () => {
                      const code = document.getElementById("join-code-input").value.trim().toUpperCase();
                      if (!code) return;
                      const { error } = await supabase.rpc("join_project_by_code", { code, joining_role: profile.role });
                      if (error) { showToast(t("invalidCodeToast")); } else { showToast(t("projectJoined")); loadProjects(); }
                    }} style={{ background: "#C9A96E", color: "#0F0E0C", border: "none", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{t("joinBtn")}</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
              {projects.map(p => (
                <div key={p.id} onClick={() => onSelect(p)} className="card" style={{ padding: 20, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#C9A96E60"} onMouseLeave={e => e.currentTarget.style.borderColor = "#2A2924"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 3 }}>{p.name}</div>{p.address && <div style={{ fontSize: 11, color: "#5A5450" }}>{p.address}</div>}</div>
                    <span className="badge" style={{ background: "#C9A96E20", color: "#C9A96E", border: "none", flexShrink: 0 }}>{{ EN_COURS: "En cours", TERMINE: "Terminé", EN_ATTENTE: "En attente", PLANIFIE: "Planifié", SUSPENDU: "Suspendu" }[p.status] || p.status}</span>
                  </div>
                  {p.client_name && <div style={{ fontSize: 11, color: "#7A7468", marginBottom: 10 }}>Client : {p.client_name}</div>}
                  <div className="pbar" style={{ height: 4, marginBottom: 4 }}><div className="pfill" style={{ width: `${p.progress}%`, background: "#C9A96E" }} /></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#5A5450" }}>
                    <span>{p.progress}% avancement</span>
                    {p.owner_id === profile.id && <span style={{ color: "#C9A96E" }}>✓ Propriétaire</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      {toast && <div className="toast">{toast}</div>}

      {/* MODAL: Rejoindre par code */}
      {showJoin && (
        <div className="modal-overlay" onClick={() => setShowJoin(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#E8E2D8" }}>{t("joinProjectTitle")}</span>
              <button className="btn" onClick={() => setShowJoin(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "24px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ fontSize: 13, color: "#7A7468", margin: 0 }}>{t("joinProjectDesc")}</p>
              <input
                placeholder={t("joinProjectPlaceholder")}
                value={joinCodeInput}
                onChange={e => setJoinCodeInput(e.target.value.toUpperCase())}
                style={{ textAlign: "center", fontSize: 28, fontWeight: 700, letterSpacing: "0.25em", padding: "14px 0" }}
                autoFocus
              />
              <button
                className="btn"
                disabled={joining || !joinCodeInput.trim()}
                onClick={async () => {
                  setJoining(true);
                  const { error } = await supabase.rpc("join_project_by_code", {
                    code: joinCodeInput.trim().toUpperCase(),
                    joining_role: profile.role,
                  });
                  if (error) {
                    showToast(t("invalidCodeToast"));
                  } else {
                    showToast(t("projectJoined"));
                    setShowJoin(false);
                    setJoinCodeInput("");
                    loadProjects();
                  }
                  setJoining(false);
                }}
                style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, border: "none" }}
              >
                {joining ? t("joining") : t("joinProjectBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#E8E2D8" }}>{t("newProject")}</span>
              <button className="btn" onClick={() => setShowCreate(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div><div className="sl" style={{ marginBottom: 6 }}>Nom du projet *</div><input placeholder={t("projectNamePlaceholder")} value={newName} onChange={e => setNewName(e.target.value)} /></div>
              <div><div className="sl" style={{ marginBottom: 6 }}>Adresse</div><input placeholder={t("addressPlaceholder")} value={newAddress} onChange={e => setNewAddress(e.target.value)} /></div>
              <div><div className="sl" style={{ marginBottom: 6 }}>Nom du client</div><input placeholder={t("clientNamePlaceholder")} value={newClient} onChange={e => setNewClient(e.target.value)} /></div>
              <div><div className="sl" style={{ marginBottom: 6 }}>Date de livraison prévue</div><input type="date" value={newDeadline} onChange={e => setNewDeadline(e.target.value)} /></div>
              <button onClick={createProject} disabled={creating || !newName.trim()} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, border: "none" }}>
                {creating ? t("creating") : t("createProjectBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
